import { useParams, useNavigate } from 'react-router-dom'
import { useGHLAnalysisDetail, useGHLAnalysisSiblings, useGHLLocationNames, useGHLContactNames } from '@/hooks/useGHLDashboard'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Copy, Check, Clock, Cpu, Mic, MessageSquare, Target, FileText, GitCommitHorizontal, Mail, Phone } from 'lucide-react'
import { type GHLAnalysis, formatCost, calculateAnalysisCost, calculateWhisperCost, formatTokenCount, formatRelativeTime, getActionColor, getSkippedByToggle, getSkipReasonLabel, truncateId } from '@/lib/ghl-dashboard'
import { ContactAvatar } from '@/components/ghl-dashboard/ContactAvatar'
import { useMemo, useState } from 'react'

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 text-xs gap-1"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : label}
    </Button>
  )
}

function FieldGrid({ fields }: { fields: Record<string, unknown> }) {
  const entries = Object.entries(fields).filter(([, v]) => v !== null && v !== undefined)
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">No fields extracted</p>

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-muted-foreground">{key.replace(/_/g, ' ')}</span>
          <span className="text-sm">{String(value)}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Pick a badge color for a pipeline stage name. Terminal stages (booked / lost)
 * get distinct colors; everything else (quoted, new lead, etc.) is "open".
 */
function getStageColor(stageName: string | undefined | null): { bg: string; text: string } {
  const s = stageName?.trim().toLowerCase() ?? ''
  if (s === 'booked') return { bg: 'bg-emerald-100', text: 'text-emerald-700' }
  if (s === 'lost') return { bg: 'bg-rose-100', text: 'text-rose-700' }
  if (!s) return { bg: 'bg-muted', text: 'text-muted-foreground' }
  return { bg: 'bg-amber-100', text: 'text-amber-700' }
}

/**
 * A single sibling event rendered as a timeline row. Factored out so both
 * opportunity sub-groups and the unassociated bucket can reuse it.
 */
function TimelineEventRow({ entry, currentId, onSelect }: {
  entry: GHLAnalysis
  currentId: string
  onSelect: (id: string) => void
}) {
  const isCurrent = entry.id === currentId
  const color = getActionColor(entry.recommended_action)
  return (
    <li className="relative">
      <span
        className={
          'absolute -left-[1.70rem] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background ' +
          (isCurrent ? 'bg-primary' : 'bg-muted-foreground/40')
        }
        aria-hidden
      />
      <button
        type="button"
        onClick={() => !isCurrent && onSelect(entry.id)}
        disabled={isCurrent}
        className={
          'w-full text-left flex items-start gap-2 text-xs py-1 px-2 -mx-2 rounded transition-colors ' +
          (isCurrent
            ? 'font-semibold cursor-default'
            : 'text-muted-foreground hover:bg-muted cursor-pointer')
        }
      >
        <span className="shrink-0 w-24 tabular-nums">
          {formatRelativeTime(entry.created_at)}
        </span>
        <Badge variant="outline" className={`${color.bg} ${color.text} text-[10px] px-1.5 py-0 h-4 shrink-0`}>
          {entry.trigger_event.replace('Message', '')}
        </Badge>
        <span className="flex-1 line-clamp-2">
          {entry.conversation_summary ?? entry.recommended_action}
        </span>
      </button>
    </li>
  )
}

/**
 * One opportunity's sub-timeline: a header with the opportunity's current
 * stage + latest price, then the events for that opportunity in chronological
 * order.
 */
function OpportunitySubTimeline({ oppId, events, currentId, onSelect }: {
  oppId: string | null
  events: GHLAnalysis[]
  currentId: string
  onSelect: (id: string) => void
}) {
  // Derive the opportunity's current stage from its LATEST stage-change event.
  // Events arrive chronologically ASC, so traverse in reverse to find the most recent.
  const latestStageChange = [...events].reverse().find(
    (e) => e.recommended_action === 'STAGE_CHANGE' && e.action_result
  )
  const currentStage = (latestStageChange?.action_result as
    { toStageName?: string } | undefined)?.toStageName
  const stageColor = getStageColor(currentStage)

  // Latest non-null priced event — the number operators care about
  const latestPriced = [...events].reverse().find((e) => e.price_quoted !== null)
  const latestPrice = latestPriced?.price_quoted ?? null

  const headerLabel = oppId === null
    ? 'Unassociated events'
    : `Opportunity ${oppId.slice(0, 8)}…`

  return (
    <div>
      <div className="flex items-center flex-wrap gap-2 mb-2 pb-2 border-b">
        <span className={
          'text-sm font-medium ' + (oppId === null ? 'text-muted-foreground italic' : '')
        }>
          {headerLabel}
        </span>
        {oppId !== null && currentStage && (
          <Badge className={`${stageColor.bg} ${stageColor.text} text-xs`}>
            {currentStage}
          </Badge>
        )}
        {oppId !== null && latestPrice !== null && (
          <span className="text-sm text-muted-foreground font-medium">
            ${latestPrice.toLocaleString()}
          </span>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {events.length} event{events.length === 1 ? '' : 's'}
        </span>
      </div>
      <ol className="space-y-2 border-l-2 border-border ml-2 pl-5">
        {events.map((entry) => (
          <TimelineEventRow
            key={entry.id}
            entry={entry}
            currentId={currentId}
            onSelect={onSelect}
          />
        ))}
      </ol>
    </div>
  )
}

function TimelineCard({ siblings, currentId, onSelect }: {
  siblings: GHLAnalysis[]
  currentId: string
  onSelect: (id: string) => void
}) {
  if (siblings.length <= 1) return null

  // Group siblings by opportunity_id; null opportunity_id → "Unassociated" bucket.
  // Sort opportunities by most-recent-activity DESC so the hottest appears first.
  // Events WITHIN each group are chronological ASC (oldest first → newest at bottom).
  const groups = useMemo(() => {
    const byOpp = new Map<string | null, GHLAnalysis[]>()
    for (const entry of siblings) {
      const key = entry.opportunity_id ?? null
      const list = byOpp.get(key)
      if (list) list.push(entry)
      else byOpp.set(key, [entry])
    }

    const result = [...byOpp.entries()].map(([oppId, events]) => {
      const sorted = [...events].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
      return {
        oppId,
        events: sorted,
        latestAt: sorted[sorted.length - 1].created_at,
      }
    })

    // Real opportunities first (by latest activity DESC), then the "Unassociated" bucket
    result.sort((a, b) => {
      if (a.oppId === null && b.oppId !== null) return 1
      if (a.oppId !== null && b.oppId === null) return -1
      return new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime()
    })

    return result
  }, [siblings])

  const opportunityCount = groups.filter((g) => g.oppId !== null).length

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <GitCommitHorizontal className="h-4 w-4" /> Timeline
          <span className="text-xs text-muted-foreground font-normal">
            ({siblings.length} event{siblings.length === 1 ? '' : 's'} across{' '}
            {opportunityCount} opportunit{opportunityCount === 1 ? 'y' : 'ies'})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {groups.map(({ oppId, events }) => (
          <OpportunitySubTimeline
            key={oppId ?? '__unassociated'}
            oppId={oppId}
            events={events}
            currentId={currentId}
            onSelect={onSelect}
          />
        ))}
      </CardContent>
    </Card>
  )
}

/**
 * Friendly labels for every GHL message-kind string. Display-only — keyed by
 * GHL's canonical `messageType` enum value. Includes activity / campaign /
 * internal kinds that the middleware's fetch layer normally filters out before
 * persisting, so the map stays correct even if filtering changes upstream.
 */
const MESSAGE_TYPE_LABELS: Record<string, string> = {
  TYPE_CALL: 'Call',
  TYPE_CUSTOM_CALL: 'Call',
  TYPE_IVR_CALL: 'IVR Call',
  TYPE_SMS: 'SMS',
  TYPE_CUSTOM_SMS: 'SMS',
  TYPE_CUSTOM_PROVIDER_SMS: 'SMS',
  TYPE_SMS_REVIEW_REQUEST: 'SMS Review Request',
  TYPE_SMS_NO_SHOW_REQUEST: 'SMS No-Show Request',
  TYPE_EMAIL: 'Email',
  TYPE_CUSTOM_EMAIL: 'Email',
  TYPE_CUSTOM_PROVIDER_EMAIL: 'Email',
  TYPE_WEBCHAT: 'Web Chat',
  TYPE_LIVE_CHAT: 'Live Chat',
  TYPE_LIVE_CHAT_INFO_MESSAGE: 'Live Chat Info',
  TYPE_GMB: 'Google Business',
  TYPE_CAMPAIGN_GMB: 'Campaign · Google Business',
  TYPE_FACEBOOK: 'Facebook',
  TYPE_FACEBOOK_COMMENT: 'Facebook Comment',
  TYPE_CAMPAIGN_FACEBOOK: 'Campaign · Facebook',
  TYPE_INSTAGRAM: 'Instagram',
  TYPE_INSTAGRAM_COMMENT: 'Instagram Comment',
  TYPE_WHATSAPP: 'WhatsApp',
  TYPE_REVIEW: 'Review',
  TYPE_CAMPAIGN_SMS: 'Campaign · SMS',
  TYPE_CAMPAIGN_CALL: 'Campaign · Call',
  TYPE_CAMPAIGN_EMAIL: 'Campaign · Email',
  TYPE_CAMPAIGN_VOICEMAIL: 'Campaign · Voicemail',
  TYPE_CAMPAIGN_MANUAL_CALL: 'Manual Campaign · Call',
  TYPE_CAMPAIGN_MANUAL_SMS: 'Manual Campaign · SMS',
  TYPE_INTERNAL_COMMENT: 'Internal Note',
  TYPE_ACTIVITY_CONTACT: 'Activity · Contact',
  TYPE_ACTIVITY_INVOICE: 'Activity · Invoice',
  TYPE_ACTIVITY_PAYMENT: 'Activity · Payment',
  TYPE_ACTIVITY_OPPORTUNITY: 'Activity · Opportunity',
  TYPE_ACTIVITY_APPOINTMENT: 'Activity · Appointment',
  TYPE_ACTIVITY_EMPLOYEE_ACTION_LOG: 'Activity · Employee Action',
}

/**
 * Map GHL's message-kind to a human-friendly channel label.
 *
 * Prefers the canonical `messageType` string (`TYPE_SMS`, `TYPE_CALL`, …).
 * Falls back to the numeric `type` code for analyses persisted before
 * `messageType` was carried through `FormattedMessage`. Unknown values are
 * de-prefixed and title-cased so a new GHL channel still reads sensibly.
 */
function formatMessageType(messageType?: string, type?: number): string {
  if (messageType && MESSAGE_TYPE_LABELS[messageType]) return MESSAGE_TYPE_LABELS[messageType]
  if (messageType) {
    return messageType
      .replace(/^TYPE_/, '')
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }
  const NUMERIC_LABELS: Record<number, string> = { 1: 'Call', 2: 'SMS', 3: 'Email' }
  if (type !== undefined && NUMERIC_LABELS[type]) return NUMERIC_LABELS[type]
  return 'Message'
}

function MessageTypeIcon({ label }: { label: string }) {
  if (/call|voicemail|ivr/i.test(label)) return <Phone className="h-3 w-3" />
  if (/email/i.test(label)) return <Mail className="h-3 w-3" />
  return <MessageSquare className="h-3 w-3" />
}

function MessageBubble({
  direction,
  body,
  dateAdded,
  messageType,
  type,
}: {
  direction: string
  body: string
  dateAdded?: string
  messageType?: string
  type?: number
}) {
  const isInbound = direction === 'inbound'
  const typeLabel = formatMessageType(messageType, type)
  return (
    <div className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isInbound ? 'bg-muted' : 'bg-primary/10'}`}>
        <Badge variant="outline" className="mb-1 h-5 gap-1 px-1.5 text-[10px] font-medium">
          <MessageTypeIcon label={typeLabel} />
          {typeLabel}
        </Badge>
        <p className="whitespace-pre-wrap">{body}</p>
        {dateAdded && (
          <p className="text-[10px] text-muted-foreground mt-1">
            {new Date(dateAdded).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  )
}

export default function GHLAnalysisDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: analysis, isLoading } = useGHLAnalysisDetail(id ?? '', !!id)
  const { data: siblings } = useGHLAnalysisSiblings(analysis, !!analysis)
  const locationNames = useGHLLocationNames()

  // Resolve contact name for the prominent header — single contact, single fetch
  const contactIds = useMemo(() => (analysis?.contact_id ? [analysis.contact_id] : []), [analysis?.contact_id])
  const { data: contactNames } = useGHLContactNames(contactIds, analysis?.location_id)
  const contact = analysis?.contact_id ? contactNames?.get(analysis.contact_id) : undefined

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (!analysis) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <p className="text-muted-foreground">Analysis not found</p>
      </div>
    )
  }

  const actionColor = getActionColor(analysis.recommended_action)
  const claudeCost = calculateAnalysisCost(analysis.input_tokens, analysis.output_tokens, analysis.model_used)
  const whisperCost = calculateWhisperCost(analysis.whisper_duration_seconds)
  const totalCost = claudeCost + whisperCost
  const locationName = locationNames.data?.get(analysis.location_id) ?? truncateId(analysis.location_id)
  const rawMessages = (analysis.raw_messages ?? []) as Array<{ direction: string; body: string; dateAdded: string; messageType?: string; type?: number }>
  const skippedByToggle = getSkippedByToggle(analysis.action_result)

  return (
    <div className="space-y-6">
      {/* Back + breadcrumb */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <span className="text-sm text-muted-foreground">
          {locationName} &middot; {formatRelativeTime(analysis.created_at)}
        </span>
      </div>

      {/* Prominent contact header — the primary anchor for cross-referencing with GHL */}
      <Card>
        <CardContent className="pt-6 pb-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <ContactAvatar
              size="lg"
              showName
              firstName={contact?.firstName}
              lastName={contact?.lastName}
              fullName={contact?.fullName}
              contactId={analysis.contact_id}
              subtitle={contact?.email ?? undefined}
            />
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold">Analysis Detail</h1>
                <Badge className={`${actionColor.bg} ${actionColor.text}`}>{actionColor.label}</Badge>
                {skippedByToggle.length > 0 && (
                  <Badge
                    variant="outline"
                    className="bg-amber-50 text-amber-800 border-amber-200 text-xs"
                    title="Per-location toggles blocked one or more intended writes"
                  >
                    Suppressed by toggle
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap justify-end">
                {contact?.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {contact.email}
                  </span>
                )}
                {contact?.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {contact.phone}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {new Date(analysis.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Toggle suppression breakdown — only when at least one write was blocked.
          Surfaces "intended X, suppressed because Y" so the dashboard reflects
          what was meant to happen vs. what actually wrote to GHL. */}
      {skippedByToggle.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  Intended {actionColor.label} — suppressed by location toggles
                </p>
                <p className="text-xs text-amber-800 mb-3">
                  The middleware planned this action but the location's settings blocked the writes below. The recommended action above is preserved so you can see what was meant to happen, while the chips below show which gates fired.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {skippedByToggle.map((reason) => (
                    <Badge
                      key={reason}
                      variant="outline"
                      className="bg-white text-amber-800 border-amber-300 text-xs"
                    >
                      {getSkipReasonLabel(reason)}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stat Cards Row */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Confidence</p>
            <p className={`text-2xl font-bold ${analysis.booking_confidence >= 85 ? 'text-green-600' : analysis.booking_confidence >= 50 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
              {analysis.booking_confidence}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Price</p>
            <p className="text-2xl font-bold">
              {analysis.price_quoted !== null ? `$${analysis.price_quoted.toLocaleString()}` : '--'}
              {analysis.price_confirmed && <span className="text-green-600 text-sm ml-1">confirmed</span>}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1 mb-1">
              <Cpu className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Claude</p>
            </div>
            <p className="text-lg font-bold">{formatCost(claudeCost)}</p>
            <p className="text-[10px] text-muted-foreground">{formatTokenCount(analysis.input_tokens)} in / {formatTokenCount(analysis.output_tokens)} out</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1 mb-1">
              <Mic className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Whisper</p>
            </div>
            <p className="text-lg font-bold">{whisperCost > 0 ? formatCost(whisperCost) : '--'}</p>
            <p className="text-[10px] text-muted-foreground">
              {analysis.whisper_duration_seconds ? `${Math.round(analysis.whisper_duration_seconds / 60)}m transcribed` : 'No calls'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Cost</p>
            <p className="text-2xl font-bold">{formatCost(totalCost)}</p>
            <p className="text-[10px] text-muted-foreground">{analysis.model_used ?? '--'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Timeline of sibling analyses (welcome SMS, Claude runs, stage changes) */}
      {siblings && siblings.length > 1 && (
        <TimelineCard
          siblings={siblings}
          currentId={analysis.id}
          onSelect={(sid) => navigate(`/admin/ghl-middleware/analysis/${sid}`)}
        />
      )}

      {/* Main Content: 2 columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column: Summary + Fields + Action */}
        <div className="space-y-6">
          {/* Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" /> Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{analysis.conversation_summary ?? 'No summary'}</p>
            </CardContent>
          </Card>

          {/* Reasoning */}
          {analysis.reasoning && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="h-4 w-4" /> Reasoning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{analysis.reasoning}</p>
              </CardContent>
            </Card>
          )}

          {/* Extracted Fields */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Extracted Fields</CardTitle>
            </CardHeader>
            <CardContent>
              <FieldGrid fields={analysis.extracted_fields ?? {}} />
            </CardContent>
          </Card>

          {/* Action Result */}
          {analysis.action_result && (
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Action Result</CardTitle>
                <CopyButton text={JSON.stringify(analysis.action_result, null, 2)} />
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(analysis.action_result, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {analysis.tags_applied && analysis.tags_applied.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Tags Applied</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {analysis.tags_applied.map((tag) => (
                    <Badge key={tag} variant="outline">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column: Messages + Raw Claude Response */}
        <div className="space-y-6">
          {/* Conversation Messages */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" /> Conversation ({rawMessages.length} messages)
              </CardTitle>
              {rawMessages.length > 0 && (
                <CopyButton text={JSON.stringify(rawMessages, null, 2)} label="Copy JSON" />
              )}
            </CardHeader>
            <CardContent>
              {rawMessages.length > 0 ? (
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {rawMessages.map((msg, i) => (
                    <MessageBubble key={i} direction={msg.direction} body={msg.body} dateAdded={msg.dateAdded} messageType={msg.messageType} type={msg.type} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No raw messages stored (older analysis)</p>
              )}
            </CardContent>
          </Card>

          {/* Raw Claude Response */}
          {analysis.raw_claude_response && (
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Cpu className="h-4 w-4" /> Raw Claude Response
                </CardTitle>
                <CopyButton text={analysis.raw_claude_response} />
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto max-h-[400px] whitespace-pre-wrap">
                  {analysis.raw_claude_response}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* IDs Footer */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(analysis.created_at).toISOString()}</span>
            <span>Analysis: <code className="bg-muted px-1 rounded">{analysis.id}</code></span>
            <span>Contact: <code className="bg-muted px-1 rounded">{analysis.contact_id}</code></span>
            {analysis.conversation_id && (
              <span>Conversation: <code className="bg-muted px-1 rounded">{analysis.conversation_id}</code></span>
            )}
            {analysis.opportunity_id && <span>Opportunity: <code className="bg-muted px-1 rounded">{analysis.opportunity_id}</code></span>}
            <span>Location: <code className="bg-muted px-1 rounded">{analysis.location_id}</code></span>
            <span>Event: {analysis.trigger_event}</span>
            <span>Messages: {analysis.message_count}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
